import { useEffect, useState } from "react";
import { authedFetch } from "@/lib/authed-fetch";
import type {
  DownloadPolicyPreflightFile,
  DownloadPolicyPreflightRequest,
  DownloadPolicyPreflightResponse,
} from "@shared/share-download-policy";

/** Advisory only: saves repeat trusted server inspection; never submit proof. */
export function useDownloadPolicyPreflight(
  enabled: boolean,
  request: DownloadPolicyPreflightRequest,
  sourceIdentity = "",
) {
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState<{
    signature: string;
    status: "pending" | "complete" | "failed";
    files: DownloadPolicyPreflightFile[];
    error: string | null;
  } | null>(null);
  const hasFiles = (request.files?.length ?? 0) + (request.fileIds?.length ?? 0) > 0;
  const active = enabled && hasFiles;
  const body = JSON.stringify(request);
  const signature = JSON.stringify([active, body, sourceIdentity, retry]);

  useEffect(() => {
    if (!active) {
      setState(null);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    setState({ signature, status: "pending", files: [], error: null });
    // Batch rapid checkbox changes before spending the server inspection budget.
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await authedFetch("/api/preview-shares/download-policy/preflight", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            signal: controller.signal,
          });
          const data = await res.json().catch(() => null) as DownloadPolicyPreflightResponse & { error?: string } | null;
          if (!res.ok) throw new Error(data?.error || `Media inspection failed (${res.status})`);
          if (!data || !Array.isArray(data.files)) throw new Error("Media inspection returned no verification results");
          if (data.files.some(file => !file || typeof file.fileName !== "string" ||
            typeof file.compatible !== "boolean" || typeof file.unverified !== "boolean" ||
            (typeof file.path !== "string" && (!Number.isInteger(file.fileId) || (file.fileId ?? 0) <= 0)))) {
            throw new Error("Media inspection returned invalid verification results");
          }
          if (!cancelled) setState({ signature, status: "complete", files: data.files, error: null });
        } catch (error) {
          if (!cancelled) setState({
            signature, status: "failed", files: [],
            error: error instanceof Error ? error.message : "Media inspection failed",
          });
        }
      })();
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [active, body, signature]);

  // Invalidate synchronously on any selection/source change, before effects run.
  const current = active && state?.signature === signature ? state : null;
  return {
    pending: active && (!current || current.status === "pending"),
    failed: active && current?.status === "failed",
    error: current?.error ?? null,
    files: current?.status === "complete" ? current.files : [],
    retry: () => setRetry(value => value + 1),
  };
}